/**
 * Servicio para manejar verificaciones automáticas de versión
 * Permite verificar periódicamente si hay nuevas versiones de la agenda
 */

import { Storage } from '@capacitor/storage';
import { checkVersionUpdate, checkAndUpdateData } from '../data/dataApi';

export interface VersionCheckConfig {
  /** Intervalo en minutos para verificar versión automáticamente */
  checkIntervalMinutes: number;
  /** Si debe mostrar notificaciones al usuario */
  showNotifications: boolean;
  /** Si debe actualizar automáticamente sin preguntar */
  autoUpdate: boolean;
}

export class VersionCheckService {
  private static instance: VersionCheckService;
  private checkInterval: NodeJS.Timeout | null = null;
  private isChecking = false;
  private checkingEventCode: string | null = null;
  private activeEventCode: string | null = null;
  private startRequestId = 0;
  private focusCheckSetup = false;
  private config: VersionCheckConfig = {
    checkIntervalMinutes: 15,
    showNotifications: true,
    autoUpdate: false
  };

  private readonly handleVisibilityChange = (): void => {
    if (!document.hidden) {
      console.log('👁️ App enfocada, verificando versión...');
      void this.checkNow();
    }
  };

  private readonly handleWindowFocus = (): void => {
    console.log('🎯 Ventana enfocada, verificando versión...');
    void this.checkNow();
  };

  static getInstance(): VersionCheckService {
    if (!VersionCheckService.instance) {
      VersionCheckService.instance = new VersionCheckService();
    }
    return VersionCheckService.instance;
  }

  /**
   * Configura el servicio de verificación de versión
   */
  configure(config: Partial<VersionCheckConfig>): void {
    this.config = { ...this.config, ...config };
    console.log('🔧 VersionCheckService configurado:', this.config);
  }

  /**
   * Inicia las verificaciones automáticas de versión
   */
  async startAutoCheck(eventCodeOverride?: string): Promise<void> {
    const requestId = ++this.startRequestId;

    try {
      const eventCode = eventCodeOverride || (await Storage.get({ key: 'eventcode' })).value;

      // Ignorar inicializaciones que terminaron después de un cambio de evento.
      if (requestId !== this.startRequestId) return;
      
      if (!eventCode || eventCode === 'nada') {
        console.log('⚠️ No hay código de evento, no se puede iniciar verificación automática');
        this.stopAutoCheck();
        return;
      }

      if (this.checkInterval) {
        clearInterval(this.checkInterval);
        this.checkInterval = null;
      }
      this.activeEventCode = eventCode;

      console.log(`🚀 Iniciando verificación automática cada ${this.config.checkIntervalMinutes} minutos`);

      // Verificar inmediatamente
      await this.performVersionCheck(eventCode);

      // El evento pudo cambiar mientras se consultaba su versión.
      if (requestId !== this.startRequestId || this.activeEventCode !== eventCode) return;

      // Configurar verificación periódica
      this.checkInterval = setInterval(async () => {
        await this.performVersionCheck(eventCode);
      }, this.config.checkIntervalMinutes * 60 * 1000);

    } catch (error) {
      console.error('❌ Error al iniciar verificación automática:', error);
    }
  }

  /**
   * Detiene las verificaciones automáticas
   */
  stopAutoCheck(): void {
    this.startRequestId += 1;

    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
      console.log('🛑 Verificación automática detenida');
    }

    this.activeEventCode = null;
  }

  /**
   * Verifica manualmente si hay actualizaciones
   */
  async checkNow(): Promise<boolean> {
    try {
      const eventCode = this.activeEventCode || (await Storage.get({ key: 'eventcode' })).value;
      
      if (!eventCode || eventCode === 'nada') {
        console.log('⚠️ No hay código de evento para verificar');
        return false;
      }

      return await this.performVersionCheck(eventCode, true);
    } catch (error) {
      console.error('❌ Error en verificación manual:', error);
      return false;
    }
  }

  /**
   * Realiza la verificación de versión
   */
  private async performVersionCheck(eventCode: string, isManual: boolean = false): Promise<boolean> {
    if (this.checkingEventCode === eventCode && !isManual) {
      console.log('⏳ Ya hay una verificación en progreso para este evento');
      return false;
    }

    this.isChecking = true;
    this.checkingEventCode = eventCode;

    try {
      console.log('🔍 Verificando versión...');
      
      const versionCheck = await checkVersionUpdate(eventCode);
      
      if (!versionCheck.hasUpdate) {
        console.log('✅ Versión actualizada');
        return false;
      }

      console.log(`🆕 Nueva versión detectada: ${versionCheck.currentVersion} → ${versionCheck.serverVersion}`);

      // Si es verificación automática y está configurado para auto-actualizar
      if (!isManual && this.config.autoUpdate) {
        console.log('🔄 Actualizando automáticamente...');
        return await checkAndUpdateData(eventCode, true);
      }

      // Si debe mostrar notificaciones
      if (this.config.showNotifications) {
        this.showUpdateNotification(versionCheck.serverVersion, isManual);
      }

      return true;

    } catch (error) {
      console.error('❌ Error en verificación de versión:', error);
      return false;
    } finally {
      if (this.checkingEventCode === eventCode) {
        this.isChecking = false;
        this.checkingEventCode = null;
      }
    }
  }

  /**
   * Muestra notificación de actualización disponible
   */
  private showUpdateNotification(newVersion: string, isManual: boolean): void {
    // Crear evento personalizado para notificar a la aplicación
    const event = new CustomEvent('versionUpdateAvailable', {
      detail: {
        newVersion,
        isManual
      }
    });
    
    window.dispatchEvent(event);

    // También mostrar en consola
    console.log(`📱 Nueva versión disponible: v${newVersion}`);
  }

  /**
   * Verifica versión cuando la app vuelve al foco
   */
  setupFocusCheck(): void {
    if (this.focusCheckSetup) return;

    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('focus', this.handleWindowFocus);
    this.focusCheckSetup = true;
  }

  teardownFocusCheck(): void {
    if (!this.focusCheckSetup) return;

    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('focus', this.handleWindowFocus);
    this.focusCheckSetup = false;
  }

  /**
   * Obtiene el estado actual del servicio
   */
  getStatus(): {
    isRunning: boolean;
    isChecking: boolean;
    config: VersionCheckConfig;
  } {
    return {
      isRunning: this.checkInterval !== null,
      isChecking: this.isChecking,
      config: this.config
    };
  }
}

// Exportar instancia singleton
export const versionCheckService = VersionCheckService.getInstance();

// Tipos para eventos personalizados
declare global {
  interface WindowEventMap {
    'versionUpdateAvailable': CustomEvent<{
      newVersion: string;
      isManual: boolean;
    }>;
  }
}
